import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";

type ContactSubmission = {
  id: string;
  name: string;
  email: string;
  message: string;
  created_at: string;
  ip_address: string | null;
  email_message_id: string | null;
};

type EmailLog = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
};

type DisplayStatus = "queued" | "sent" | "failed" | "no-attempt";
type StatusFilter = "all" | DisplayStatus;

export const Route = createFileRoute("/admin/contact-log")({
  head: () => ({
    meta: [
      { title: "Admin | Contact email log | Scott Cunningham" },
      { name: "description", content: "Contact email log for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Contact email log | Scott Cunningham" },
      { property: "og:description", content: "Contact email log for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Contact email log | Scott Cunningham" },
      { name: "twitter:description", content: "Contact email log for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ContactLogPage,
});

function normalizeStatus(status?: string | null): DisplayStatus {
  if (!status) return "no-attempt";
  if (status === "sent") return "sent";
  if (status === "pending") return "queued";
  return "failed";
}

function ContactLogPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  const reload = async () => {
    setBusy(true);
    const [contactResult, logResult] = await Promise.all([
      supabase
        .from("contact_submissions")
        .select("id, name, email, message, created_at, ip_address, email_message_id")
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("email_send_log")
        .select("id, message_id, template_name, recipient_email, status, error_message, created_at")
        .eq("template_name", "contact-notification")
        .order("created_at", { ascending: false })
        .limit(1000),
    ]);
    setSubmissions((contactResult.data as ContactSubmission[]) ?? []);
    setLogs((logResult.data as EmailLog[]) ?? []);
    setBusy(false);
  };

  useEffect(() => {
    if (!isAdmin) return;
    reload();
  }, [isAdmin]);

  const emailAttempts = useMemo(() => {
    const map = new Map<string, { messageId: string; latest: EmailLog; firstAt: string }>();
    for (const row of logs) {
      if (!row.message_id) continue;
      const existing = map.get(row.message_id);
      if (!existing) {
        map.set(row.message_id, {
          messageId: row.message_id,
          latest: row,
          firstAt: row.created_at,
        });
        continue;
      }
      if (new Date(row.created_at).getTime() > new Date(existing.latest.created_at).getTime()) {
        existing.latest = row;
      }
      if (new Date(row.created_at).getTime() < new Date(existing.firstAt).getTime()) {
        existing.firstAt = row.created_at;
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(a.firstAt).getTime() - new Date(b.firstAt).getTime(),
    );
  }, [logs]);

  const latestByMessageId = useMemo(
    () => new Map(emailAttempts.map((attempt) => [attempt.messageId, attempt.latest])),
    [emailAttempts],
  );

  const resolvedRows = useMemo(() => {
    const usedFallbacks = new Set<string>();
    return submissions.map((submission) => {
      let log = submission.email_message_id
        ? (latestByMessageId.get(submission.email_message_id) ?? null)
        : null;
      if (!log) {
        const submittedAt = new Date(submission.created_at).getTime();
        const fallback = emailAttempts.find((attempt) => {
          if (usedFallbacks.has(attempt.messageId)) return false;
          const firstAt = new Date(attempt.firstAt).getTime();
          return firstAt >= submittedAt && firstAt - submittedAt <= 120_000;
        });
        if (fallback) {
          log = fallback.latest;
          usedFallbacks.add(fallback.messageId);
        }
      }
      return { submission, log, displayStatus: normalizeStatus(log?.status) };
    });
  }, [emailAttempts, latestByMessageId, submissions]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resolvedRows.filter(({ submission, log, displayStatus }) => {
      if (status !== "all" && displayStatus !== status) return false;
      if (!q) return true;
      return (
        submission.name.toLowerCase().includes(q) ||
        submission.email.toLowerCase().includes(q) ||
        submission.message.toLowerCase().includes(q) ||
        (log?.error_message ?? "").toLowerCase().includes(q)
      );
    });
  }, [query, resolvedRows, status]);

  const stats = useMemo(() => {
    const counts: Record<DisplayStatus, number> = {
      queued: 0,
      sent: 0,
      failed: 0,
      "no-attempt": 0,
    };
    for (const row of resolvedRows) {
      counts[row.displayStatus]++;
    }
    return counts;
  }, [resolvedRows]);

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <AdminNav title="Contact email log" />

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat
          label="Queued"
          value={stats.queued}
          active={status === "queued"}
          onClick={() => setStatus("queued")}
        />
        <Stat
          label="Sent"
          value={stats.sent}
          active={status === "sent"}
          onClick={() => setStatus("sent")}
        />
        <Stat
          label="Failed"
          value={stats.failed}
          active={status === "failed"}
          onClick={() => setStatus("failed")}
        />
        <Stat
          label="No attempt"
          value={stats["no-attempt"]}
          active={status === "no-attempt"}
          onClick={() => setStatus("no-attempt")}
        />
      </div>

      <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="grid gap-2 md:grid-cols-[18rem_12rem]">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search submissions or errors…"
          />
          <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}>
            <SelectTrigger aria-label="Filter send status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="no-attempt">No attempt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            {rows.length} of {submissions.length} submissions
          </span>
          <Button size="sm" variant="outline" onClick={reload} disabled={busy}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[11rem]">Submitted</TableHead>
              <TableHead>Sender</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-[9rem]">Email status</TableHead>
              <TableHead className="w-[15rem]">Recipient</TableHead>
              <TableHead className="w-[11rem]">Updated</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {busy && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!busy && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  No contact submissions match.
                </TableCell>
              </TableRow>
            )}
            {!busy &&
              rows.map(({ submission, log, displayStatus }) => (
                <TableRow key={submission.id} className="align-top">
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(submission.created_at).toLocaleString()}
                    <div className="mt-1 font-mono text-[10px]">
                      {submission.ip_address ?? "IP unknown"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{submission.name}</div>
                    <a className="text-sm underline" href={`mailto:${submission.email}`}>
                      {submission.email}
                    </a>
                  </TableCell>
                  <TableCell className="max-w-[24rem]">
                    <p className="line-clamp-4 whitespace-pre-line text-sm text-muted-foreground">
                      {submission.message}
                    </p>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={displayStatus} rawStatus={log?.status} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {log?.recipient_email ? (
                      <a className="underline" href={`mailto:${log.recipient_email}`}>
                        {log.recipient_email}
                      </a>
                    ) : (
                      "N/A"
                    )}
                    {submission.email_message_id && (
                      <div
                        className="mt-1 truncate font-mono text-[10px] text-muted-foreground"
                        title={submission.email_message_id}
                      >
                        {submission.email_message_id}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {log?.created_at ? new Date(log.created_at).toLocaleString() : "N/A"}
                  </TableCell>
                  <TableCell className="max-w-[22rem] text-xs text-muted-foreground">
                    {log?.error_message ? (
                      <span className="whitespace-pre-wrap">{log.error_message}</span>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatusBadge({ status, rawStatus }: { status: DisplayStatus; rawStatus?: string | null }) {
  if (status === "sent") return <Badge>Sent</Badge>;
  if (status === "queued") return <Badge variant="secondary">Queued</Badge>;
  if (status === "no-attempt") return <Badge variant="outline">No attempt</Badge>;
  return <Badge variant="destructive">{rawStatus === "dlq" ? "Failed (DLQ)" : "Failed"}</Badge>;
}

function Stat({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md border px-4 py-3 text-left transition ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:bg-muted/50"
      }`}
    >
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 font-display text-2xl tabular-nums">{value}</div>
    </button>
  );
}
